// @ts-nocheck

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, Search, Heart, Eye, X, Filter, Plus } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { motion } from 'framer-motion';

interface SavedArtwork {
  id: string;
  title: string;
  artist: string;
  year: number;
  medium: string;
  dimensions: string;
  image: string;
  category: string;
  estimatedValue: { low: number; high: number };
  gallery: string;
  savedAt: string;
  notes?: string;
}

const SavedArtworksTab: React.FC = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const queryClient = useQueryClient();

  const { data: savedArtworks = [], isLoading } = useQuery({
    queryKey: ['userSavedArtworks', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Try RPC first
      const { data, error } = await supabase.rpc('get_user_saved_artworks', {
        p_user_id: user.id
      });

      if (!error && Array.isArray(data)) {
        // Normalize RPC shape to UI expectations
        return (data as any[]).map((row: any) => ({
          saved_artwork_id: row.saved_artwork_id,
          artwork_id: row.artwork_id,
          title: row.artwork_title,
          artist_name: row.artist_name,
          year: row.artwork_year,
          medium: row.medium,
          dimensions: row.dimensions,
          image_url: row.artwork_image_url,
          condition: row.condition,
          estimate_low: row.estimate_low,
          estimate_high: row.estimate_high,
          saved_at: row.saved_at,
          notes: row.user_notes,
        }));
      }

      // Fallback to two-step query if RPC fails
      console.warn('SavedArtworksTab: RPC get_user_saved_artworks failed, using two-step fallback', error);
      const { data: savedRows, error: savedErr } = await supabase
        .from('saved_artworks')
        .select('id, artwork_id, created_at, notes')
        .eq('user_id', user.id as any)
        .order('created_at', { ascending: false });

      if (savedErr || !Array.isArray(savedRows) || savedRows.length === 0) {
        if (savedErr) console.error('SavedArtworksTab: fetching saved rows failed:', savedErr);
        return [];
      }

      const artworkIds = [...new Set(savedRows.map((r: any) => r.artwork_id).filter(Boolean))];
      if (artworkIds.length === 0) return [];

      const { data: artworks, error: artErr } = await supabase
        .from('artworks')
        .select(`
          id,
          title,
          image_url,
          year,
          medium,
          dimensions,
          condition,
          estimate_low,
          estimate_high,
          artist:artist_id ( name )
        `)
        .in('id', artworkIds as any);

      if (artErr) {
        console.error('SavedArtworksTab: fetching artworks failed:', artErr);
        return [];
      }

      const idToArtwork: Record<string, any> = {};
      (artworks || []).forEach((a: any) => { idToArtwork[a.id] = a; });

      return savedRows.map((row: any) => {
        const a = idToArtwork[row.artwork_id] || {};
        return {
          saved_artwork_id: row.id,
          artwork_id: row.artwork_id,
          title: a.title || null,
          artist_name: a.artist?.name || null,
          year: a.year ?? null,
          medium: a.medium ?? null,
          dimensions: a.dimensions ?? null,
          image_url: a.image_url ?? null,
          condition: a.condition ?? null,
          estimate_low: a.estimate_low ?? null,
          estimate_high: a.estimate_high ?? null,
          saved_at: row.created_at,
          notes: row.notes ?? null,
        };
      });
    },
    enabled: !!user?.id,
  });

  const removeSavedArtworkMutation = useMutation({
    mutationFn: async (artworkId: string) => {
      const { error } = await supabase
        .from('saved_artworks')
        .delete()
        .eq('artwork_id', artworkId)
        .eq('user_id', user?.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSavedArtworks', user?.id] });
      toast({
        title: "Artwork removed",
        description: "The artwork has been removed from your saved artworks.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove the artwork. Please try again.",
        variant: "destructive",
      });
    },
  });

  const categories = ['all', 'Florida Highwaymen', 'Contemporary', 'Traditional', 'Abstract'];

  const filteredArtworks = Array.isArray(savedArtworks) ? savedArtworks.filter((artwork: any) => {
    const matchesSearch = (artwork.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (artwork.artist_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (artwork.medium || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || artwork.category === categoryFilter;
    return matchesSearch && matchesCategory;
  }) : [];

  const removeSavedArtwork = (artworkId: string) => {
    removeSavedArtworkMutation.mutate(artworkId);
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-8 space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <h2 className="text-2xl font-light text-white mb-2">Saved Artworks</h2>
            <p className="text-white/70 font-light">Your curated collection of favorite artworks</p>
          </div>
          
          {/* Filters */}
          <div className="flex gap-4 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-96">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                placeholder="Search artworks, artists, or medium..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/10 backdrop-blur-xl border-white/20 text-white placeholder:text-white/40 focus:border-white/40 rounded-xl"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48 bg-white/10 backdrop-blur-xl border-white/20 text-white rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Content */}
        {isLoading ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
              <CardContent className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400 mx-auto mb-4"></div>
                <p className="text-white/70 font-light">Loading your saved artworks...</p>
              </CardContent>
            </Card>
          </motion.div>
        ) : filteredArtworks.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-white/5 backdrop-blur-xl border-white/20 border-dashed shadow-xl">
              <CardContent className="p-12 text-center">
                {searchQuery || categoryFilter !== 'all' ? (
                  <>
                    <Search className="h-16 w-16 text-white/40 mx-auto mb-6" />
                    <h3 className="text-xl font-light text-white mb-2">No artworks found</h3>
                    <p className="text-white/60 font-light">Try adjusting your search or filter criteria</p>
                  </>
                ) : (
                  <>
                    <Package className="h-16 w-16 text-white/40 mx-auto mb-6" />
                    <h3 className="text-xl font-light text-white mb-2">No saved artworks yet</h3>
                    <p className="text-white/60 font-light mb-6">Save artworks you're interested in to keep track of them</p>
                    <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg hover:shadow-xl transform hover:scale-105">
                      <Plus className="h-4 w-4 mr-2" />
                      Browse Artworks
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {filteredArtworks.map((artwork: any, index: number) => (
              <motion.div
                key={artwork.saved_artwork_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
                  <CardContent className="p-8">
                    <div className="flex gap-6">
                      {/* Artwork Image */}
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <img 
                            src={artwork.image_url || 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400'} 
                            alt={artwork.title || 'Untitled'}
                            className="w-40 h-40 object-cover rounded-2xl border border-white/20 shadow-lg"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
                        </div>
                      </div>

                      {/* Artwork Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-4">
                          <div className="space-y-2">
                            <h3 className="text-2xl font-medium text-white">{artwork.title || 'Untitled'}</h3>
                            <p className="text-xl text-white/80 font-light">{artwork.artist_name || 'Unknown Artist'}</p>
                            <p className="text-white/60 font-light">{artwork.year ? `${artwork.year} • ` : ''}{artwork.medium || 'Unknown medium'}</p>
                            <p className="text-white/60 font-light">{artwork.dimensions || 'Dimensions not specified'}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeSavedArtwork(artwork.artwork_id)}
                              disabled={removeSavedArtworkMutation.isPending}
                              className="text-white/60 hover:text-red-400 hover:bg-red-500/10 p-2 rounded-xl"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-8 mb-6">
                          <div>
                            <p className="text-xs text-white/50 uppercase tracking-wider font-medium mb-2">Estimated Value</p>
                            <p className="text-xl font-bold text-white">
                              {Number.isFinite(Number(artwork.estimate_low)) && Number.isFinite(Number(artwork.estimate_high))
                                ? `$${Number(artwork.estimate_low).toLocaleString()} - $${Number(artwork.estimate_high).toLocaleString()}`
                                : Number.isFinite(Number(artwork.estimate_low))
                                  ? `$${Number(artwork.estimate_low).toLocaleString()}+`
                                  : Number.isFinite(Number(artwork.estimate_high))
                                    ? `Up to $${Number(artwork.estimate_high).toLocaleString()}`
                                    : 'Not available'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-white/50 uppercase tracking-wider font-medium mb-2">Date Added</p>
                            <p className="text-xl font-bold text-white">{new Date(artwork.saved_at).toLocaleDateString()}</p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-6 border-t border-white/10">
                          <div className="space-y-1">
                            <p className="text-xs text-white/50 flex items-center gap-2">
                              <Heart className="h-3 w-3" />
                              Saved: {new Date(artwork.saved_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-3" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

export default SavedArtworksTab;